import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, MapPin, User, Mail, Phone, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingCompany = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    timezone: 'Europe/Berlin',
    language: 'de',
    location_name: '',
    location_address: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name) {
      toast.error('Company name is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/onboarding/company`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company setup completed!');
      navigate('/onboarding/drivers');
    } catch (error) {
      console.error('Company setup error:', error);
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout currentStep={1}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Company Information
          </h2>
          <p className="text-gray-500 mt-1">
            Let's start by setting up your company profile
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your Company GmbH"
                required
              />
            </div>

            <div>
              <Label htmlFor="address">Company Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street, City, ZIP"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <User className="h-5 w-5" />
              Contact Person
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Name</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  placeholder="contact@company.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+49 30 12345678"
              />
            </div>
          </div>

          {/* Primary Location */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Location (Optional)
            </h3>

            <div>
              <Label htmlFor="location_name">Location Name</Label>
              <Input
                id="location_name"
                name="location_name"
                value={formData.location_name}
                onChange={handleChange}
                placeholder="Main Branch"
              />
            </div>

            <div>
              <Label htmlFor="location_address">Location Address</Label>
              <Input
                id="location_address"
                name="location_address"
                value={formData.location_address}
                onChange={handleChange}
                placeholder="Branch address if different"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Next: Add Drivers'}
            </Button>
          </div>
        </form>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingCompany;
